import { Modal, ModalBody, ModalFooter, ModalHeader, OverlayTrigger, Tooltip } from "react-bootstrap"
import Input from "../../form-components/Input";
import Button from "../../form-components/Button";
import { useEffect, useState } from "react";
import importAll from "../../chat-component/images";
import { useDispatch, useSelector } from "react-redux";
import { showSummaryPrompt } from "../../../features/summaryPrompt/showSummaryPromptSlice";
import Loader from "../../../common/loader/Loader";
import { updateSummaryPrompt } from "../../../features/summaryPrompt/updateSummaryPromptSlice";
import { createSummaryPrompt } from "../../../features/summaryPrompt/createSummaryPromptSlice";
import { getSummaryPrompt } from "../../../features/summaryPrompt/getSummaryPromptSlice";
import { OK } from "../../../constants/statusCode";
import { wordCount } from "../../../utils/utils";

const images = importAll(
    require.context("../../../assets/images/", false, /\.(png|jpe?g|svg|GIF|webp)$/)
);

const NAME_LENGTH = 20, PROMPT_LENGTH = 700;

const SummaryPromptModal = ({
    isVisible,
    toggleFunction,
    summaryPrompt,
    modalTitle,
    showRetrainDocumentsPopup,
}) => {
    const dispatch = useDispatch();
    const { responseData: summaryPromptData } = useSelector((state) => state?.showSummaryPromptReducer);
    const { showIsLoading, editIsLoading, createIsLoading } = useSelector(
        (state) => ({
          showIsLoading: state?.showSummaryPromptReducer?.isLoading,
          editIsLoading: state?.updateSummaryPromptReducer?.isLoading,
          createIsLoading: state?.createSummaryPromptReducer?.isLoading,
        })
    );
    const [input, setInput] = useState({
        id: "",
        uuid: "",
        name: "",
        prompt: "",
        is_default: false,
        created_at: "",
    });
    const [inputError, setInputError] = useState({});
    const [isNameFocused, setIsNameFocused] = useState(false);
    const [isPromptFocused, setIsPromptFocused] = useState(false);

    const handleChangeInput = (key, value) => {
        input[key] = value;
        setInput({ ...input });
    }

    const save = async () => {
        let error = {};
        
        if (!input.name.trim()) {
            error.name = "Please enter name";
        }
        else if (input.name.length > NAME_LENGTH) {
            error.name = `Name should contain less than ${NAME_LENGTH} characters.`;
        }

        if (!input.prompt.trim()) {
            error.prompt = "Please enter prompt";
        }
        else if (input.prompt.length > PROMPT_LENGTH) {
            error.prompt = `Prompt should contain less than ${PROMPT_LENGTH} characters.`;
        }
    
        setInputError({ ...error });
    
        if (Object.keys(error).length === 0) {
            let updateResponse = null;
            input.is_default = input.is_default ? 1 : 0;
            if (input.uuid) {
                updateResponse = await dispatch(updateSummaryPrompt({ promptData: input, uuid: input.uuid }));
            }
            else {
                await dispatch(createSummaryPrompt(input));
            }
            toggleFunction();
            await dispatch(getSummaryPrompt({ perPage: 10, currentPage: 1 }));
            if (updateResponse?.payload.status === OK && input.uuid && input.is_default) {
                showRetrainDocumentsPopup();
            }
        }
    };
    
    useEffect(() => {
        setInput({
            id: "",
            uuid: "",
            name: "",
            prompt: "",
            is_default: false,
            created_at: "",
        });
        setInputError({});
        if (isVisible && summaryPrompt.uuid) {
            dispatch(showSummaryPrompt(summaryPrompt.uuid));
        }
    }, [summaryPrompt]);

    useEffect(() => {
        if (summaryPromptData) {
            setInput({
                id: summaryPromptData.id ?? "",
                uuid: summaryPromptData.uuid ?? "",
                name: summaryPromptData.name ?? "",
                prompt: summaryPromptData.prompt ?? "",
                is_default: summaryPromptData.is_default?.toString() === "1",
                created_at: summaryPromptData.created_at ?? "",
            });
        }
    }, [summaryPromptData]);
    
    return (
        <>
            {(showIsLoading || editIsLoading || createIsLoading) && <Loader />}
            <Modal
                data-bs-keyboard="false"
                onHide={toggleFunction}
                show={isVisible}
                className="modal fade addsummary-prompt-modal"
                centered={true}
                dialogClassName="common-modal"
            >
                <ModalHeader closeButton onHide={toggleFunction}>
                    <h3 className="modal-title">{modalTitle}</h3>
                </ModalHeader>
                <ModalBody>
                    <div className="row">
                        <div className="col-md-12">
                            <div className="form-group">
                                <label className="form-label">
                                    Name*
                                    <OverlayTrigger
                                        placement="right"
                                        overlay={<Tooltip className="summaryname-tooltip cmn-summary-tooltip" id={input.uuid + "-name"}>Name depicts an alias to your prompt.</Tooltip>}
                                    >
                                        <img src={images["info.svg"]} alt="" className="ms-2 cursor-pointer" />
                                    </OverlayTrigger>
                                </label>
                                <Input
                                    type="text"
                                    placeholder="Personal Assistant Chatbot"
                                    className="form-control"
                                    value={input.name}
                                    onChange={(e) => handleChangeInput("name", e.target.value)}
                                    onFocus={() => setIsNameFocused(true)}
                                    onBlur={() => setIsNameFocused(false)}
                                />
                                <span className="text-danger">{inputError.name}</span>
                                <span className="chat-form-note position-static float-end">
                                    {isNameFocused
                                        ? <>{input.name.length}/{NAME_LENGTH}</>
                                        : input.name.length
                                    }
                                    {" "} character{input.name.length > 1 ? "s" : ""}
                                </span>
                            </div>
                        </div>
                        <div className="col-md-12">
                            <div className="form-group mb-0">
                                <label className="form-label">
                                    Prompt*
                                    <OverlayTrigger
                                        placement="right"
                                        overlay={<Tooltip className="summaryprompt-tooltip cmn-summary-tooltip" id={input.uuid + "-prompt"}>A 'Summary Prompt' is a brief input or question that helps generate a concise summary or overview of information. Think of it as the question or topic you want summarized.</Tooltip>}
                                    >
                                        <img src={images["info.svg"]} alt="" className="ms-2 cursor-pointer" />
                                    </OverlayTrigger>
                                </label>
                                <textarea
                                    type="text"
                                    placeholder="Imagine you have a personal assistant chatbot. Start a conversation with it, asking it to help you schedule your week, set reminders, and find information online.Add Prompt here"
                                    className="form-control sum-prompt-textarea"
                                    value={input.prompt}
                                    onChange={(e) => handleChangeInput("prompt", e.target.value)}
                                    rows={"4"}
                                    style={{ height: "auto" }}
                                    onFocus={() => setIsPromptFocused(true)}
                                    onBlur={() => setIsPromptFocused(false)}
                                />
                                <span className="text-danger">{inputError.prompt}</span>
                                <span className="chat-form-note position-static float-end">
                                    {isPromptFocused
                                        ? <>{input.prompt.length}/{PROMPT_LENGTH}</>
                                        : input.prompt.length
                                    }
                                    {" "} character{input.prompt.length > 1 ? "s" : ""}
                                </span>
                            </div>
                        </div>
                    </div>
                </ModalBody>
                <ModalFooter>
                    <div className="form-group mb-0">
                        <div className="form-check text-start footer-check">
                            <Input
                                onChange={(e) => handleChangeInput("is_default", !input.is_default)}
                                className="form-check-input"
                                type="checkbox"
                                id="is_default"
                                name="is_default"
                                checked={input.is_default}
                            />
                            <label className="form-check-label" htmlFor="is_default">
                                Set as default
                            </label>
                        </div>
                    </div>
                    <Button
                        type="button"
                        className="btn btn-primary-md m-0"
                        onClick={save}
                    >
                        Save
                    </Button>
                </ModalFooter>
            </Modal>
        </>
    )
}

export default SummaryPromptModal
